//core
import React, {useCallback, useContext, useEffect, useState} from "react";
import { useSelector } from "react-redux";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDebounce } from "usehooks-ts";

//selectors
import { selectListData, selectListError } from "../../../../../store/admin/subAffDealsV2/selectors";
import { selectDeleteConditionData } from "../../../../../store/admin/subAffDealsV2/selectors";

//hooks
import { usePermissions } from "../../../../../store/common/configuration/usePermissions";
import { useSubAffDealsV2 } from "../../../../../store/admin/subAffDealsV2/useSubAffDealsV2";
import useDidMountEffect from "../../../../../customHooks/useDidMountEffect";
import { getIds } from "../../../Brands/utils";

//context
import { TableContext } from "../TableContext";
import { GlobalContext } from "../../../../../context/GlobalContext";

//types
import { OrderType } from "../../../../affiliate/Trackers";
import { ISubAffDealsV2FilterDataType } from "../../types";

//styles
import { useHeaderStyles } from "../../../../UI/PageHeaderStyles/styles";

//components
import TableActions from "../TableActions";
import TableData from "../../../../common/TableData";
import ErrorData from "../../../../common/ErrorData";
import MobileList from "../MobileList";
import DrawerFilter from "../DrawerFilter";


interface IListSubAffDealsProps {
  search: string;
}

export const initSubAffDealsV2FilterState: ISubAffDealsV2FilterDataType = {
	platformIds: [],
	createdByIds: [],
}

const ListSubAffDeals: React.FC<IListSubAffDealsProps> = ({
  search,
}): JSX.Element => {
  const { hasPermissions } = usePermissions();
	const headerStyles = useHeaderStyles();
	const { t } = useTranslation();

  const context = useContext(GlobalContext);

  const { getList, clearList, clearDeleteCondition } = useSubAffDealsV2();

  const data = useSelector(selectListData);
  const error = useSelector(selectListError);
  const successDelete = useSelector(selectDeleteConditionData);

  const hasDelete = hasPermissions(["api3.admin.subaffiliatedealcontroller.subaffiliate_deal_delete"]);
  const hasEdit = hasPermissions(["api3.admin.subaffiliatedealcontroller.subaffiliate_deal_update"]);
	const hasList = hasPermissions(["api3.admin.subaffiliatedealcontroller.subaffiliate_deal_list"]);
	const hasView = hasPermissions(["api3.admin.subaffiliatedealcontroller.subaffiliate_deal_view"]);

	const [filter, setFilter] = useState<ISubAffDealsV2FilterDataType>(context.admSubAffDealsV2Filter);
	const debouncedFilter = useDebounce<ISubAffDealsV2FilterDataType>(filter, 150);

  const [order, setOrder] = useState<OrderType>(context.admSubAffDealsV2Order);
  const [page, setPage] = useState<number>(context.admSubAffDealsV2Page);
  const [rowsPerPage, setRowsPerPage] = useState(context.admSubAffDealsV2Rows);

  const [actionsCount, setActionsCount] = useState<number>(0);

	const mobile = useMediaQuery('(max-width:767px)');

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      setPage(newPage);
      context.setAdmSubAffDealsV2Page(newPage);
    }, [setPage]);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setRowsPerPage(+event.target.value);
    context.setAdmSubAffDealsV2Rows(+event.target.value);
  },[setRowsPerPage, setPage]);

  const handleChangeOrder = useCallback((newOrder: OrderType) => {
    context.setAdmSubAffDealsV2Order(newOrder);
    setOrder(newOrder);
  }, [setOrder]) ;

  /* action calculation */
  useEffect(() => {
    if (hasDelete) {
      setActionsCount((prevState) => {
        return prevState + 1
      });
    }
  }, [hasDelete]);
  useEffect(() => {
    if (hasEdit) {
      setActionsCount((prevState) => {
        return prevState + 1
      });
    }
  }, [hasEdit]);
	useEffect(() => {
    if (hasView) {
      setActionsCount((prevState) => {
        return prevState + 1
      });
    }
  }, [hasView]);
  /* end action calculation */

  useDidMountEffect(() => {
    setPage(0);
    context.setAdmSubAffDealsV2Page(0);
    const data = getPayloadData(1);

    getList(data);
  }, [search, order, rowsPerPage, debouncedFilter]);

  useEffect(() => {
    context.setAdmSubAffDealsV2Page(page);
    const data = getPayloadData(page + 1)
    getList(data);
  }, [page]);

  useEffect(() => {
    return () => {
      clearList();
    };
  }, []);

  useEffect(() => {
    if (!!successDelete) {
      clearDeleteCondition();
      handleGenerateWithCurrentState();
    }
  }, [successDelete]);

  const handleGenerateWithCurrentState = () => {
    const data = getPayloadData(page + 1);
    getList(data);
  };

  const getPayloadData = (page: number) => {
    return {
      page: page,
	    perPage: rowsPerPage,
      order: order,
	    filter: {
		    search: !!search ? search : null,
		    ...(!!filter.platformIds.length ? {platformId: getIds(filter.platformIds)} : {}),
		    ...(!!filter.createdByIds.length ? {createdById: getIds(filter.createdByIds)} : {}),
	    },
    }
  };

  return (
    <TableContext.Provider value={{
      onGenerateCurrentState: handleGenerateWithCurrentState,
    }}>
      <Box>
	      <DrawerFilter
		      initialFilter={filter}
		      setUpdateFilter={setFilter}
		      order={order}
		      setOrder={setOrder}
		      onGenerateReport={handleGenerateWithCurrentState}
	      >
		      {!mobile && (
			      <Typography className={headerStyles.pageTitle} variant="h6">{t("admin.sub_aff_deals.title")}</Typography>
		      )}
	      </DrawerFilter>
        {!!data && hasList && (<>
          {!mobile ? (
	          <TableData
	            list={data.list}
	            columns={data.columns}
	            order={order}
	            setOrder={handleChangeOrder}
	            page={page}
	            rowsPerPage={rowsPerPage}
	            totalEntries={+data.totalEntries}
	            onPageChange={handleChangePage}
	            onRowsPerPageChange={handleChangeRowsPerPage}
	            actionsCount={!!actionsCount ? actionsCount : 1}
	            {...(!!actionsCount ? { ActionsComponent: TableActions } : {})}
	            translationColumnsPrefix="columns_for_display.table.generate_report."
	            startRowNum={data.startRowNum}
	            endRowNum={data.endRowNum}
	          />
          ) : (
						<MobileList
							data={data}
	            currentPage={page}
	            setPage={handleChangePage}
						/>
          )}
        </>)}
        {
          error && <ErrorData title={error.status} text={error.detail} />
        }
      </Box>
    </TableContext.Provider>
  );
};

export default ListSubAffDeals;
